/**
 * Every feedback enum is a row in `value_sets_master`, never a constant in this
 * codebase. The form reads them here so that adding a componentType (or a question
 * type, or a UI mode) through the Value Sets screen gives the UI a working option
 * with no release.
 *
 * There is deliberately NO hardcoded fallback list: a stale fallback silently
 * disagrees with what the server will accept, and a definition saved against a
 * code the server rejects fails at submit with a confusing 400. If a set is
 * missing, the picker is empty and `seeded` is false — the form says so, which
 * points at the real fix (run the bootstrap / seed the value set).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { valueSetsService } from '../../services/value-sets.service';

export interface VsOption { label: string; value: string }

/** Value-set keys, seeded by app/api/v1/services/feedback_bootstrap.py. */
export const VS = {
  componentType: 'feedbackComponentType',
  questionType: 'feedbackQuestionType',
  status: 'feedbackDefinitionStatus',
  contextDataType: 'feedbackContextDataType',
  signalRole: 'feedbackSignalRole',
  smeRole: 'feedbackSmeRole',
  /** "<COMPONENT_TYPE>:<contextKey>" — the context worth capturing for that type */
  componentContextKeys: 'feedbackComponentContextKeys',
} as const;

function useValueSet(key: string) {
  const q = useQuery({
    queryKey: ['value-set-items', key],
    queryFn: () => valueSetsService.getItems(key),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const items = q.data ?? [];
  const options: VsOption[] = items.map((i) => ({
    label: i.label || i.code,
    value: i.code,
  }));
  // `items` carries the flags the options cannot — see useAiComponentTypes.
  return { options, items, seeded: options.length > 0, loading: q.isLoading };
}

export const useComponentTypes = () => useValueSet(VS.componentType);

/**
 * Which component types describe model output, read from the value set rather
 * than listed here.
 *
 * The item flag `isAi` is what the server classifies by too
 * (`feedback_component_scope.py`), so the AI screen and the Feedback module can
 * never disagree about what belongs where, and adding an AI component type is a
 * row edit on the Value Sets screen rather than a change in two repositories.
 *
 * `null` while the set is loading or when NO item carries the flag — which every
 * caller reads as "no opinion, show everything", the same fallback
 * `useComponentContextKeys` uses. Guessing here would hide options that the
 * server would have accepted.
 */
export const useAiComponentTypes = (): Set<string> | null => {
  const { items } = useValueSet(VS.componentType);
  return useMemo(() => {
    const flagged = items.filter((i) => i.isAi === true).map((i) => i.code);
    return flagged.length ? new Set(flagged) : null;
  }, [items]);
};
export const useQuestionTypes = () => useValueSet(VS.questionType);
export const useDefinitionStatuses = () => useValueSet(VS.status);
export const useContextDataTypes = () => useValueSet(VS.contextDataType);
export const useSignalRoles = () => useValueSet(VS.signalRole);
export const useSmeRoles = () => useValueSet(VS.smeRole);

/**
 * Per-component-type configuration, read from value sets whose codes are
 * "<COMPONENT_TYPE>:<value>".
 *
 * The fallback matters as much as the data: a component type with NO rows gets
 * `null`, and every caller treats null as "no opinion — show everything". A type
 * someone adds to feedbackComponentType therefore keeps working immediately; it just
 * gets no tailoring until rows are added for it.
 */
function usePairedValueSet(key: string, componentType?: string | null) {
  const q = useQuery({
    queryKey: ['value-set-items', key],
    queryFn: () => valueSetsService.getItems(key),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const forType = useMemo(() => {
    const type = (componentType || '').toUpperCase();
    if (!type || !q.data?.length) return null;
    const values = q.data
      .map((i) => i.code)
      .filter((c) => c.toUpperCase().startsWith(`${type}:`))
      .map((c) => c.slice(type.length + 1));
    return values.length ? values : null;
  }, [q.data, componentType]);

  return { values: forType, loading: q.isLoading };
}

/** Context keys worth capturing for this component type (null = no suggestions). */
export const useComponentContextKeys = (componentType?: string | null) =>
  usePairedValueSet(VS.componentContextKeys, componentType);

/**
 * Which question types actually take a list of choices. Derived from the CODE,
 * not from a hardcoded list of types — if a new select-like type is added to the
 * value set it only needs its code to end in _SELECT to behave correctly here.
 */
export const isChoiceType = (questionType?: string | null) =>
  !!questionType && questionType.toUpperCase().endsWith('_SELECT');

export const isRatingType = (questionType?: string | null) =>
  (questionType || '').toUpperCase() === 'RATING';

export const isTextType = (questionType?: string | null) =>
  (questionType || '').toUpperCase() === 'TEXT';

export const isNumberType = (questionType?: string | null) =>
  (questionType || '').toUpperCase() === 'NUMBER';
